import { useAuth0 } from '@auth0/auth0-react';
import classNames from 'classnames';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardFooter, CardHeader } from 'reactstrap';
import { BackendService, Project, PunchEntry } from '../service/backend-service';
import { useStoreDispatch, useStoreSelector } from '../store/hooks';
import { getWorkSet, setWorkSet } from '../store/store-core';
import { errorMessage } from '../util/error-utils';
import { StyledToast } from '../util/styled-toast';
import { TimeUtils } from '../util/time-utils';
import ProjectSelector from './project-selector';
import PunchContainer from './punch-container';
import PunchEntryElement from './punch-entry-element';
import WelcomePage from './welcome-page';

function Home() {
  const [currentMonth, setCurrentMonth] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [latestPunches, setLatestPunches] = useState<Array<PunchEntry>>([]);
  const [monthlyPunches, setMonthlyPunches] = useState<Array<PunchEntry>>([]);
  const [projectSelectedLatestPunches, setProjectSelectedLatestPunches] = useState<Project|undefined>(undefined);
  const [projectSelectedMonthlyPunches, setProjectSelectedMonthlyPunches] = useState<Project|undefined>(undefined);

  let dispatch = useStoreDispatch();
  let workSet = useStoreSelector(getWorkSet);

  let auth0 = useAuth0();
  const { isAuthenticated, isLoading } = auth0;

  const service = useMemo(() => {
    return new BackendService(auth0);
  }, [auth0]);

  useEffect(() => {
    setLoading(true);
    service.getWorkSet()
      .then(work => dispatch(setWorkSet(work)))
      .catch((ex: any) => StyledToast.error(errorMessage(ex)))
      .finally(() => setLoading(false));
  }, [service, dispatch]);

  useEffect(() => {
    setLoading(true);
    service.getLatestPunches()
      .then(latestPunches => setLatestPunches(latestPunches))
      .catch((ex: any) => StyledToast.error(errorMessage(ex)))
      .finally(() => setLoading(false));
  }, [workSet, service]);

  useEffect(() => {
    let utc = moment().utc();
    let startOfMonth = utc.startOf('month').format('YYYY-MM-DD');
    let endOfMonth = utc.endOf('month').format('YYYY-MM-DD');
    setLoading(true);
    setCurrentMonth(utc.format('MMMM'));
    service.getPunchesByDate(startOfMonth, endOfMonth)
      .then(monthly => setMonthlyPunches(monthly))
      .catch((ex: any) => StyledToast.error(errorMessage(ex)))
      .finally(() => setLoading(false));
  }, [workSet, service]);

  let filterPunches = (punches: Array<PunchEntry>, selectedProject?: Project) : Array<PunchEntry> => {
    return punches.filter(proj => selectedProject == null || proj.project.id === selectedProject.id);
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <WelcomePage />;
  }

  const onSaveEntry = (updated: PunchEntry) => {
    setLatestPunches(latestPunches.map(punch => punch.id === updated.id ? updated : punch));
    setMonthlyPunches(monthlyPunches.map(punch => punch.id === updated.id ? updated : punch));

    let workSetByProject = workSet.find(work => work.project.id === updated.project.id);

    //This should trigger the project box to update if is an openPuch matching by ID or is closed (in case user removed endDate we need to re-open counter)
    if ((updated.end === undefined && workSetByProject?.openPunch === undefined) || updated.id === workSetByProject?.openPunch?.id) {
      dispatch(setWorkSet(
        workSet.map(work => work === workSetByProject ? {
          ...work,
          openPunch: updated
        } : work)
      ));
    }
  }

  return (
    <>
      <Card className='mt-3'>
        <CardHeader>
          <i className='fas fa-fw fa-stopwatch me-2' />
          Projects being worked on
        </CardHeader>
        <CardBody className={classNames({
          'row row-cols-xs-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4': true,
          'pb-0': !loading
        })}>
          {
            loading ?
              <div className="loading-spinner text-center w-100">
                <div className="spinner-border text-warning" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
              : workSet.map(work => <PunchContainer key={work.project.id} work={work} />)
          }
        </CardBody>
      </Card>
      <div className='mt-3 row row-cols-xs-1 row-cols-sm-1 row-cols-md-2'>
        <div className='col'>
          <Card>
            <CardHeader>
              <i className='far fa-fw fa-clock me-2' />
              Latest Punches
              <ProjectSelector projects={workSet.map(work=>work.project)} onChange={project => setProjectSelectedLatestPunches(project)}/>
            </CardHeader>
            <CardBody className={classNames({
              'p-0': !loading
            })}>
              {
                loading ?
                  <div className="loading-spinner text-center w-100">
                    <div className="spinner-border text-warning" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                  : <ul className="list-group">
                    {
                      filterPunches(latestPunches, projectSelectedLatestPunches)
                        .map((punchEntry, index) => <PunchEntryElement key={index} punchEntry={punchEntry} onSave={onSaveEntry} />)
                    }
                  </ul>
              }
            </CardBody>
            <CardFooter>
              <span className='float-end fw-bold'>Total: {TimeUtils.totalPunchesTime(filterPunches(latestPunches, projectSelectedLatestPunches))}</span>
            </CardFooter>
          </Card>
        </div>
        <div className='col'>
          <Card>
            <CardHeader>
              <i className='far fa-fw fa-calendar-days me-2' />
              Punches in <b>{currentMonth}</b><ProjectSelector projects={workSet.map(work=>work.project)} onChange={project => setProjectSelectedMonthlyPunches(project)}/>
            </CardHeader>
            <CardBody className={classNames({
              'p-0': !loading
            })}>
              {
                loading ?
                  <div className="loading-spinner text-center w-100">
                    <div className="spinner-border text-warning" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                  : <ul className="list-group">
                    {
                       filterPunches(monthlyPunches, projectSelectedMonthlyPunches)
                        .map((punchEntry, index) => <PunchEntryElement key={index} punchEntry={punchEntry} onSave={onSaveEntry} />)
                    }
                  </ul>
              }
            </CardBody>
            <CardFooter>
              <span className='float-end fw-bold'>Total: {TimeUtils.totalPunchesTime(filterPunches(monthlyPunches, projectSelectedMonthlyPunches))}</span>
            </CardFooter>
          </Card>
        </div>
      </div>
    </>
  );
}

export default Home;
